import { CSSProperties, useCallback } from "react";
import { defineMessages, useIntl } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { IconCameraX, IconEyeSpark, IconHeart, IconHeartPin, IconLock } from "@tabler/icons-react";
import { AnyPeriod, classNames, toGQLPeriod } from "@ct-react/core";
import { LocaleLink, useLocaleFormatter } from "@ct-react/locale";
import { BookingOption } from "@ct-react/calendar";
import { buildArticleUrl } from "@shared/url";
import { AnyArticleDetails, isAnnualArticle, isRentalArticle, isSaleArticle, isSeasonalArticle } from "@shared/graphql";
import { pricing } from "../i18n/sharable-defs";
import { Ratio } from "../models/common";
import { SkeletonableProps } from "../tools/components";
import ImagesCarousel from "./medias/images-carousel";
import EquipmentResume from "./equipment/equipment-resume";
import { useWishlist } from "../contexts/wishlist";
import "./article-card.scss";

export const card = defineMessages({
  novelty: { id: "cards.tags.novelty", defaultMessage: "Nouveauté" },
  recommended: { id: "cards.tags.recommended", defaultMessage: "Coup de coeur" },
  reserved: { id: "cards.tags.reserved", defaultMessage: "Réservé" },
  sold: { id: "cards.tags.sold", defaultMessage: "Vendu" },
  added_wishlist_title: { id: "cards.added-wishlist.title", defaultMessage: "Coup de cœur enregistré !" },
  added_wishlist: { id: "cards.added-wishlist", defaultMessage: "Le produit a été ajouté à votre liste d'envie !" },
  removed_wishlist_title: { id: "cards.removed-wishlist.title", defaultMessage: "C'est noté !" },
  removed_wishlist: { id: "cards.removed-wishlist", defaultMessage: "Le produit a été retiré de votre liste d'envie !" }
});

type Props = AnyArticleDetails & { suggestion?: BookingOption };

const ArticleCard = (
  {
    loading,
    className,
    data
  }: SkeletonableProps<Props>) => {

  const intl = useIntl();
  const { print } = useLocaleFormatter();
  const { has, toggle } = useWishlist();

  const not_in_wishlist = !data || !has(data.id);

  const addToWishlist = (event: React.FormEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!data) return;

    const res = toggle(data);
  };

  const buildTarget = useCallback((data: AnyArticleDetails, period?: AnyPeriod) => {
    const url = buildArticleUrl(data);
    if (!isRentalArticle(data) || !period) return url;
    const formatted = toGQLPeriod(period);
    return `${url}&b_in=${formatted[0]}&b_out=${formatted[1]}`;
  }, []);

  const formatSuggestion = useCallback((opt: BookingOption) => {
    const period = print.period(opt.period, true, true);
    return opt.priceOnDemand
      ? `${intl.formatMessage(pricing.onDemand)} ${period}`
      : `${print.price(opt.price.amount, { trailingZeroDisplay: "stripIfInteger" })} ${period}`
  }, [ intl ]);

  const imageRatio: Ratio = "3/2";
  const wrapperClasses = classNames("article-card", className, {
    "no-image": !loading && data?.images.length == 0
  });

  if (loading || !data)
    return (
      <article className={wrapperClasses}>
        <Skeleton className="card-pictures" style={{ aspectRatio: imageRatio }} />
        <div className="card-resume">
          <Skeleton className="location" width="40%" />
          <Skeleton className="title" inline={true} />
          <Skeleton className="price" width="65%" />
        </div>
      </article>);

  return (
    <article className={wrapperClasses}>
      <LocaleLink to={buildTarget(data, data.suggestion?.period)}>

        <button className={classNames("add-wishlist-btn",{ activ : !not_in_wishlist })} onClick={addToWishlist}>
            <IconHeart/>
        </button>

        <div className="card-pictures"
             style={{ "--ccv-img-ratio": imageRatio } as CSSProperties}>
          {data.images.length > 0
            ? <ImagesCarousel ratio={imageRatio} data={data.images} />
            : <IconCameraX className="x-icon" />
          }
          <ul className="badges">
            {data.novelty &&
              <li>
                <IconEyeSpark className="b-icon" />
                {intl.formatMessage(card.novelty)}
              </li>
            }
            {data.recommended &&
              <li>
                <IconHeartPin className="b-icon" />
                {intl.formatMessage(card.recommended)}
              </li>
            }
            {isSaleArticle(data) &&
              <>
                {data.reserved &&
                  <li>
                    <IconLock className="b-icon" />
                    {intl.formatMessage(card.reserved)}
                  </li>
                }
                {data.sold &&
                  <li>
                    <IconLock className="b-icon" />
                    {intl.formatMessage(card.sold)}
                  </li>
                }
              </>
            }
          </ul>
        </div>

        <div className="card-resume">
          <span className="location">{data!.address.city} - {data!.address.street}</span>
          <h1 className="title">{ data!.title.value }</h1>

          <EquipmentResume data={data}/>

          {isRentalArticle(data) && <>
            <span className="price">
              {!!data.suggestion
                ? formatSuggestion(data.suggestion)
                : (data.lowestPrice.onDemand
                  ? intl.formatMessage(pricing.onDemand)
                  : intl.formatMessage(pricing.lowest, {
                    type: data.lowestPrice.type,
                    price: print.price(data.lowestPrice.value, { trailingZeroDisplay: "stripIfInteger" })
                  }))
              }
            </span>
          </>}

          {isSeasonalArticle(data) && <>
            <span className="price">
              {(!data.price || data.price.onDemand)
                ? intl.formatMessage(pricing.onDemand)
                : (data.price.isMonthly ?
                  intl.formatMessage(pricing.byMonth, {
                    price: print.price(data.price.value, { trailingZeroDisplay: "stripIfInteger" })
                  })
                  : print.price(data.price.value, { trailingZeroDisplay: "stripIfInteger" }))
              }
            </span>
          </>}

          {isAnnualArticle(data) && <>
            <span className="price">
              {data.price.onDemand
                ? intl.formatMessage(pricing.onDemand)
                : intl.formatMessage(pricing.byMonth, {
                  price: print.price(data.price.value, { trailingZeroDisplay: "stripIfInteger" })
                })
              }
            </span>
          </>}

          {isSaleArticle(data) && <>
            <span className="price">
              {data.price.onDemand
                ? intl.formatMessage(pricing.onDemand)
                : print.price(data.price.value, { trailingZeroDisplay: "stripIfInteger" })
              }
            </span>
          </>}

        </div>
      </LocaleLink>
    </article>);

}

export default ArticleCard;
